import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { VariableSizeList, ListChildComponentProps, areEqual } from 'react-window';
import { BooleanParam, NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { Badge, Box, Fab, Link, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Add, FilterAlt, Photo } from '@mui/icons-material';
import { CarModel } from 'models/StockModel';
import { PartyType } from 'models/PartyModel';
import { CompanyType } from 'models/CompanyModel';
import useCarModals from 'modules/cars/hooks/useCarModals';
import { CarsViewProps } from 'modules/cars/views/CarsDesktop';
import CarDocCell from 'modules/cars/common/CarDocCell';
import CarCargoCell from 'modules/cars/common/CarCargoCell';
import ProblemMark from 'modules/cars/common/ProblemMark';
import PartyButton from 'modules/cars/common/PartyButton';
import auth from 'recoil/atoms/auth';


type ItemProps = {
    style: React.CSSProperties,
    index: number,
    item: any
}

const YardTitle = ({item, index, style}: ItemProps) => {
    return (
        <ListItem
            style={style}
            key={index}
            component="div"
        >
            <ListItemText
                primary={item.title}
            />
        </ListItem>
    )
};

const CompanyTitle = ({item, index, style}: ItemProps) => {
    return (
        <ListItem
            style={style}
            key={index}
            component="div"
        >
            <ListItemText
                sx={{
                    textAlign: 'center',
                    '& .MuiTypography-root': {
                        fontWeight: 'bold',
                    },
                }}
                primary={item.title}
            />
        </ListItem>
    )
};


type CarItemProps = {
    style: React.CSSProperties,
    index: number,
    car: CarModel
    modals?: any,
    yardSelected?: boolean,
}

const CarItemSimple = ({car, index, style}: CarItemProps) => {
    const navigate = useNavigate();

    return (
        <ListItem
            style={style}
            key={index}
            component="div"
            disablePadding
            sx={{
                bgcolor: 'background.paper',
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    height: '1px',
                    width: '100%',
                    bgcolor: 'grey.300',
                },
            }}
        >
            <ListItemButton
                sx={{
                    justifyContent: 'space-between',
                }}
                onClick={() => navigate(car.url!)}
            >
                <ListItemText
                    primary={car.model}
                />
                <ListItemText
                    sx={{
                        textAlign: 'right',
                    }}
                    primary={car.chassisNumber}
                />
                <ListItemText
                    sx={{
                        ml: 2,
                        flexGrow: 0,
                    }}
                    secondary={car.yard?.location}
                />
                <ListItemText
                    sx={{
                        ml: 2,
                        flexGrow: 0,
                    }}
                    primary={
                        <Badge badgeContent={car.photoCount} color="secondary">
                            <Photo color={car.albums.length ? undefined : 'disabled'}/>
                        </Badge>
                    }
                />
            </ListItemButton>
        </ListItem>
    )
};

const CarItemFull = ({car, index, style, modals, yardSelected}: CarItemProps) => {
    const {t} = useTranslation('cars');
    const navigate = useNavigate();

    return (
        <ListItem
            style={style}
            key={index}
            component="div"
            disablePadding
            sx={{
                bgcolor: 'background.paper',
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    height: '1px',
                    width: '100%',
                    bgcolor: 'grey.300',
                },
            }}
        >
            <ListItemButton
                sx={{
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                }}
            >
                <ListItemText
                    sx={{
                        maxWidth: '40%',
                        '& span': {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        },
                    }}
                    onClick={() => navigate(car.url!)}
                >
                    {car.model} <ProblemMark car={car}/>
                </ListItemText>

                <ListItemText
                    sx={{
                        textAlign: 'right',
                    }}
                    primary={car.chassisNumber}
                    onClick={() => navigate(car.url!)}
                />

                <ListItemText
                    sx={{
                        ml: 2,
                        flexGrow: 0,
                    }}
                    primary={
                        <Badge badgeContent={car.photoCount} color="secondary">
                            <Photo color={car.albums.length ? undefined : 'disabled'}/>
                        </Badge>
                    }
                    onClick={() => navigate(car.url!)}
                />

                <Box sx={{flexBasis: '100%', height: 0,}}></Box>

                <ListItemText
                    sx={{
                        width: '30%',
                    }}
                >
                    {car.voyage ?
                        <Link href={`/voyage/${car.voyage.id}`}>{car.voyage.number}</Link> :
                        t('no_voyage')
                    }
                </ListItemText>

                <ListItemText
                    sx={{
                        textAlign: 'right',
                    }}
                >
                    <PartyButton
                        car={car}
                        party={car.parties.consignee}
                        type={PartyType.Consignee}
                        compact
                    />
                    <PartyButton
                        car={car}
                        party={car.parties.notify}
                        type={PartyType.Notify}
                        compact
                    />
                    <PartyButton
                        car={car}
                        party={car.parties.dealer}
                        type={PartyType.Dealer}
                        compact
                    />
                </ListItemText>

                <ListItemText
                    sx={{
                        textAlign: 'right',
                    }}
                >
                    <CarDocCell
                        car={car}
                    />
                </ListItemText>

                <ListItemText
                    sx={{
                        textAlign: 'right',
                        ml: 2,
                        flexGrow: 0,
                    }}
                >
                    <CarCargoCell
                        car={car}
                        openInnerCargoModal={modals.openInnerCargoModal}
                    />
                </ListItemText>

                <Box sx={{flexBasis: '100%', height: 0,}}></Box>

                <ListItemText
                    sx={{
                        flexGrow: 0,
                    }}
                    secondary={`${yardSelected ? '' : car.yard?.name} ${car.yard?.location || ''}`}
                />
            </ListItemButton>
        </ListItem>
    )
};

const Row = memo(({data, index, style}: ListChildComponentProps) => {
    const item = data.items[index];

    if (item.type === 'total')
        return <YardTitle
            item={item}
            index={index}
            style={style}
        />;

    if (item.type === 'yard')
        return <YardTitle
            item={item}
            index={index}
            style={style}
        />;

    if (item.type === 'company')
        return <CompanyTitle
            item={item}
            index={index}
            style={style}
        />;

    return data.simpleView ?
        <CarItemSimple
            car={item.car}
            index={index}
            style={style}
        /> :
        <CarItemFull
            car={item.car}
            yardSelected={item.yardSelected}
            index={index}
            style={style}
            modals={data.modals}
        />;
}, areEqual);


const CarsMobile = ({
                        cars,
                        yards,
                        query,
                        filterInputs,
                        canViewCompany,
                        carCountArrived,
                    }: CarsViewProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const modals = useCarModals();
    const [filter, setFilter] = useQueryParams({
        yard: NumberParam,
        shipper: NumberParam,
        voyage: NumberParam,
        state: StringParam,
        photos: BooleanParam,
    });
    const listRef = React.createRef<VariableSizeList>();

    const processed = useMemo(() => {
        const temp = [...cars];

        temp.sort((a, b) =>
            ((a.yard?.id || 0) - (b.yard?.id || 0)) ||
            a.company.id - b.company.id ||
            a.photoCount - b.photoCount
        );

        const ret: any[] = [
            {
                type: 'total',
                title: `${t('cars_pending')} (${cars.length}), ${t('on_yard')} (${carCountArrived})`
            },
        ];

        let prevCompany = 0,
            prevYard = -1;

        temp.forEach(c => {
            const yard = c.yard?.id || 0;

            if (prevYard !== yard) {
                ret.push({type: 'yard', title: yard ? `${c.yard!.port} ${c.yard!.name}` : t('no_yard')});
                prevYard = yard;
            }

            if (canViewCompany && prevCompany !== c.company.id) {
                ret.push({type: 'company', title: c.company.name});
                prevCompany = c.company.id;
            }

            ret.push({type: 'car', car: {...c, url: `/car/${c.id}${query}`}, yardSelected: !!filter.yard});
        });

        return ret;
    }, [canViewCompany, carCountArrived, cars, query, t]);

    useEffect(() => {
        listRef.current?.resetAfterIndex(1, false);
    }, [listRef, processed]);

    const getItemSize = my.type === CompanyType.PortAgent ?
        () => 48 :
        (idx: number) => processed[idx].type === 'car' ? 96 : 48;

    return (
        <>
            <VariableSizeList
                ref={listRef}
                height={window.innerHeight - 50}
                width="100%"
                itemSize={getItemSize}
                itemCount={processed.length}
                itemData={{
                    simpleView: my.type === CompanyType.PortAgent,
                    items: processed,
                    modals,
                }}
                overscanCount={5}
            >
                {Row}
            </VariableSizeList>

            <Fab
                aria-label="filters"
                onClick={() => modals.openFilterModal(filterInputs)
                    .then(data => setTimeout(() => setFilter(data), 500))
                }
                sx={{
                    position: "fixed",
                    top: theme => theme.spacing(7),
                    right: theme => theme.spacing(1)
                }}
                size="small"
            >
                <FilterAlt/>
            </Fab>

            {my.permissions.has('cars create') &&
                <Fab
                    color="primary"
                    aria-label="add car"
                    sx={{
                        position: "fixed",
                        bottom: theme => theme.spacing(2),
                        right: theme => theme.spacing(2)
                    }}
                    onClick={modals.openAddCarModal}
                >
                    <Add/>
                </Fab>
            }
        </>
    );
}

export default CarsMobile;
